import styled, { css } from 'styled-components';

// Media query breakpoints
const breakpoints = {
  sm: '576px',
  md: '768px',
  lg: '992px',
  xl: '1200px'
};

// Media query helper
const media = Object.keys(breakpoints).reduce((acc, label) => {
  acc[label] = (...args) => css`
    @media (min-width: ${breakpoints[label]}) {
      ${css(...args)}
    }
  `;
  return acc;
}, {});

// Main Typography Components
export const Title = styled.h1`
  font-size: ${props => props.theme.typography.fontSizes.xxl};
  font-weight: ${props => props.theme.typography.fontWeights.bold};
  color: ${props => props.color || props.theme.colors.text.primary};
  margin: 0;
  margin-bottom: ${props => props.theme.spacing.xs};
  padding: 0;
  text-align: ${props => props.align || 'left'};
  font-family: ${props => props.theme.typography.fontFamily};

  ${media.md`
    font-size: ${props => props.theme.typography.fontSizes.xxxl};
    margin-bottom: ${props => props.theme.spacing.sm};
  `}
`;

export const Subtitle = styled.h2`
  font-size: ${props => props.theme.typography.fontSizes.lg};
  font-weight: ${props => props.theme.typography.fontWeights.regular};
  color: ${props => props.color || props.theme.colors.text.secondary};
  margin: 0;
  margin-bottom: ${props => props.theme.spacing.xl};
  padding: 0;
  text-align: ${props => props.align || 'left'};
  font-family: ${props => props.theme.typography.fontFamily};

  ${media.md`
    font-size: ${props => props.theme.typography.fontSizes.xl};
  `}
`;

export const Heading = styled.h3`
  font-size: ${props => props.theme.typography.fontSizes.lg};
  font-weight: ${props => props.theme.typography.fontWeights.semibold};
  color: ${props => props.color || props.theme.colors.text.primary};
  margin: 0;
  margin-bottom: ${props => props.theme.spacing.md};
  padding: 0;
  text-align: ${props => props.align || 'left'};
  font-family: ${props => props.theme.typography.fontFamily};

  ${media.md`
    font-size: ${props => props.theme.typography.fontSizes.xl};
  `}
`;

export const Text = styled.p`
  font-size: ${props => {
    const size = props.size || 'md';
    switch (size) {
      case 'xs': return props.theme.typography.fontSizes.xs;
      case 'sm': return props.theme.typography.fontSizes.sm;
      case 'lg': return props.theme.typography.fontSizes.lg;
      case 'xl': return props.theme.typography.fontSizes.xl;
      case 'xxl': return props.theme.typography.fontSizes.xxl;
      case 'xxxl': return props.theme.typography.fontSizes.xxxl;
      default: return props.theme.typography.fontSizes.md;
    }
  }};
  color: ${props => props.color || props.theme.colors.text.primary};
  margin: ${props => props.margin || '0'};
  margin-bottom: ${props => props.marginBottom || props.theme.spacing.xs};
  padding: 0;
  font-weight: ${props => {
    switch (props.weight) {
      case 'thin': return props.theme.typography.fontWeights.thin;
      case 'bold': return props.theme.typography.fontWeights.bold;
      case 'semibold': return props.theme.typography.fontWeights.semibold;
      default: return props.theme.typography.fontWeights.regular;
    }
  }};
  text-align: ${props => props.align || 'left'};
  line-height: 1.5;
  font-family: ${props => props.theme.typography.fontFamily};

  ${media.md`
    font-size: ${props => {
      const size = props.size || 'md';
      switch (size) {
        case 'xs': return props.theme.typography.fontSizes.xs;
        case 'sm': return props.theme.typography.fontSizes.sm;
        case 'lg': return props.theme.typography.fontSizes.lg;
        case 'xl': return props.theme.typography.fontSizes.xl;
        case 'xxl': return props.theme.typography.fontSizes.xxl;
        case 'xxxl': return props.theme.typography.fontSizes.xxxl;
        default: return props.theme.typography.fontSizes.md;
      }
    }};
    margin-bottom: ${props => props.marginBottom || props.theme.spacing.sm};
  `}

  ${props => props.parentFlexDirection === 'column' && css`
    &:last-child {
      margin-bottom: 0;
    }
  `}
`;

// Form-specific typography
export const Label = styled.label`
  display: block;
  font-size: ${props => props.theme.typography.fontSizes.sm};
  font-weight: ${props => props.theme.typography.fontWeights.semibold};
  color: ${props => props.theme.colors.text.secondary};
  margin: 0;
  margin-bottom: ${props => props.theme.spacing.xs};
  padding: 0;
  font-family: ${props => props.theme.typography.fontFamily};
`;

// Status/Message Typography
export const ErrorText = styled(Text)`
  color: ${props => props.theme.colors.error};
  margin-bottom: ${props => props.theme.spacing.md};
  font-size: ${props => props.theme.typography.fontSizes.sm};
  text-align: center;
  min-height: 1.25rem;
`;

export const SuccessText = styled(Text)`
  color: ${props => props.theme.colors.success};
  margin-bottom: ${props => props.theme.spacing.md};
  font-size: ${props => props.theme.typography.fontSizes.sm};
  text-align: center;
  min-height: 1.25rem;
`;

export const WarningText = styled(Text)`
  color: ${props => props.theme.colors.warning};
  margin-bottom: ${props => props.theme.spacing.md};
  font-size: ${props => props.theme.typography.fontSizes.sm};
  text-align: center;
  min-height: 1.25rem;
`;

// Card-specific typography
export const CardTitle = styled.h3`
  font-size: ${props => props.theme.typography.fontSizes.md};
  font-weight: ${props => props.theme.typography.fontWeights.semibold};
  color: ${props => props.theme.colors.text.secondary};
  margin: 0;
  margin-bottom: ${props => props.theme.spacing.md};
  padding: 0;
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};
  justify-content: flex-start;
  font-family: ${props => props.theme.typography.fontFamily};
`;

export const CardContent = styled.div`
  font-size: ${props => props.theme.typography.fontSizes.md};
  font-family: ${props => props.theme.typography.fontFamily};
  margin: 0;
  padding: 0;
`;

// Utility text components
export const Caption = styled.span`
  font-size: ${props => props.theme.typography.fontSizes.xs};
  color: ${props => props.color || props.theme.colors.text.secondary};
  font-weight: ${props => props.theme.typography.fontWeights.regular};
  font-family: ${props => props.theme.typography.fontFamily};
  margin: 0;
  padding: 0;
`;

export const Badge = styled.span`
  display: inline-block;
  font-size: ${props => props.theme.typography.fontSizes.xs};
  font-weight: ${props => props.theme.typography.fontWeights.semibold};
  color: ${props => props.theme.colors.text.inverse};
  background-color: ${props => props.color || props.theme.colors.primary};
  padding: ${props => props.theme.spacing.xs} ${props => props.theme.spacing.sm};
  border-radius: ${props => props.theme.borderRadius.pill};
  font-family: ${props => props.theme.typography.fontFamily};
  margin: 0;
`;

// Link typography
export const Link = styled.a`
  font-size: ${props => props.theme.typography.fontSizes.sm};
  color: ${props => props.theme.colors.primary};
  text-decoration: none;
  font-weight: ${props => props.theme.typography.fontWeights.semibold};
  font-family: ${props => props.theme.typography.fontFamily};
  cursor: pointer;
  transition: color 0.2s ease;
  margin: 0;
  padding: 0;

  &:hover {
    color: ${props => props.theme.colors.secondary};
    text-decoration: underline;
  }

  &:visited {
    color: ${props => props.theme.colors.primary};
  }
`;

// Special typography for login/welcome messages
export const WelcomeTitle = styled(Title)`
  text-align: center;
  margin-bottom: ${props => props.theme.spacing.lg};
  font-size: ${props => props.theme.typography.fontSizes.xl};

  ${media.md`
    font-size: ${props => props.theme.typography.fontSizes.xxl};
  `}
`;